/**
 * The threads' hooks into shared screens (2026-10-29), kept here so the
 * shared files take one line each: the channel header's Threads button, a
 * message's "Create thread" / "Open thread" (its menu, its toolbar, a
 * phone's long-press sheet), the joined threads under a channel in the
 * sidebar, and a thread full screen on a phone.
 */
import {
  isThreadChannelType,
  Routes,
  type Channel,
  type Message,
  type Snowflake,
} from '@neosocial/protocol';
import { pushBack } from '@neosocial/ui/back';
import { useEffect, useRef, type MouseEvent } from 'react';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { flagOn, useFlag } from '../store/config';
import { updateData, useData, useDataShallow } from '../store/data';
import { unreadOf } from '../store/selectors';
import { goHub, useUi } from '../store/ui';
import { Icon, IconBtn } from '../ui/kit';
import { openMenu, type MenuItem } from '../ui/overlay';
import {
  canStartFrom,
  closeThread,
  closeThreadPanel,
  openThread,
  openThreadCreate,
  openThreadList,
  threadPerms,
  useThreadUi,
} from './actions';
import { putThread, sidebarThreadsOf } from './store';
import { threadMenuItems } from './ThreadPanel';

type T = (k: string, v?: Record<string, string | number>) => string;

/** The channel header's Threads button: the list beside the channel (text channels only). */
export function ThreadsHeaderButton({ channel }: { channel: Channel }) {
  const { t } = useT();
  const on = useFlag('threads');
  const open = useThreadUi((s) => s.panel?.channelId === channel.id);
  if (!on || channel.type !== 0 || !channel.guild_id) return null;
  return (
    <IconBtn
      icon="thread"
      className="ch-threads"
      label={t('th.threads')}
      tipPos="bottom"
      pressed={open}
      onClick={() => (open ? closeThreadPanel() : openThreadList(channel.id))}
    />
  );
}

/** A message's thread items: Create thread (Create Public Threads), or Open thread once it has one. */
export function threadMessageItems(t: T, m: Message, channel: Channel): MenuItem[] {
  const d = useData.getState();
  if (!flagOn('threads') || !channel.guild_id || isThreadChannelType(channel.type)) return [];
  const th = d.channels[m.id];
  if ((th && th.parent_id === m.channel_id) || m.thread)
    return [{ label: t('th.msg.open'), icon: 'thread', run: () => openThread(th ?? m.thread!) }];
  if (!canStartFrom(d, m, channel)) return [];
  return [
    {
      label: t('th.msg.create'),
      icon: 'thread',
      run: () => openThreadCreate(channel.id, m.id),
    },
  ];
}

/** The hover toolbar's Create thread (Discord's), where one may be started. */
export function ThreadToolButton({ m, channel }: { m: Message; channel: Channel }) {
  const { t } = useT();
  const on = useFlag('threads');
  const may = useData((d) => on && canStartFrom(d, m, channel));
  if (!may) return null;
  return (
    <button
      type="button"
      aria-label={t('th.msg.create')}
      data-tip={t('th.msg.create')}
      onClick={() => openThreadCreate(channel.id, m.id)}
    >
      <Icon name="thread" size="m" />
    </button>
  );
}

/* -------------------------------- sidebar -------------------------------- */

/** The joined, active threads under a text channel in the sidebar (Discord's). */
export function SidebarThreads({ parent }: { parent: Channel }) {
  const { t } = useT();
  const on = useFlag('threads');
  const list = useDataShallow((d) => (on ? sidebarThreadsOf(d, parent.id).map((c) => c.id) : []));
  const sel = useUi((u) => (u.hub?.kind === 'channel' ? u.hub.channelId : null));
  if (!list.length) return null;
  return (
    <ul className="ns-th-side" aria-label={t('th.side.label', { name: parent.name ?? '' })}>
      {list.map((id) => (
        <SidebarThread key={id} id={id} sel={sel === id} />
      ))}
    </ul>
  );
}

function SidebarThread({ id, sel }: { id: Snowflake; sel: boolean }) {
  const { t, fmt } = useT();
  const c = useData((d) => d.channels[id]);
  const st = useDataShallow((d) => unreadOf(d, id));
  const p = useDataShallow((d) => threadPerms(d, c));
  const unread = useData((d) => {
    const x = d.channels[id];
    return !!x?.member && (st.unread || st.mentions > 0);
  });
  if (!c) return null;
  const status = st.mentions ? 'mention' : unread ? 'unread' : undefined;
  const menu = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const me = useData.getState().me?.id;
    const items = threadMenuItems(t, useData.getState().channels[id] ?? c, {
      manage: p.manage,
      send: p.send,
      meId: me,
      unread: st.unread || st.mentions > 0,
    });
    if (items.length)
      openMenu(e.clientX || e.clientY ? { x: e.clientX, y: e.clientY } : e.currentTarget, items);
  };
  return (
    <li>
      <button
        type="button"
        className="ch ns-th-ch"
        data-type="text"
        data-ch={id}
        data-st={status}
        aria-current={sel ? 'page' : undefined}
        aria-label={
          t('th.side.row', { name: c.name ?? '' }) +
          (st.mentions
            ? ', ' + t('hub.ch.mentions', { n: st.mentions })
            : unread
              ? ', ' + t('hub.ch.unread')
              : '')
        }
        tabIndex={sel ? 0 : -1}
        onClick={() => openThread(c)}
        onContextMenu={menu}
      >
        <span className="ns-th-ch-spine" aria-hidden="true" />
        <span className="ch-n" dir="auto">
          {c.name}
        </span>
        {st.mentions ? (
          <span className="ch-end">
            <b className="nx-badge">{fmt.num(st.mentions)}</b>
          </span>
        ) : null}
      </button>
    </li>
  );
}

/* --------------------------------- phone --------------------------------- */

/**
 * A phone shows an open thread full screen (§12): its own pane with the
 * thread panel's header; Back (the header's, the edge swipe, the system's)
 * returns to its channel.
 */
export function useThreadBack(thread: Channel | null, shown = true): void {
  const ref = useRef(thread);
  useEffect(() => {
    ref.current = thread;
  });
  const id = thread?.id;
  // only while the thread is on screen: a phone's main pane opening after it
  // (from the list) goes on the stack first, so Back closes the thread first
  useEffect(() => {
    if (!id || !shown) return;
    return pushBack(() => {
      const th = ref.current;
      if (th) closeThread(th);
    });
  }, [id, shown]);
}

/**
 * A channel the store doesn't hold may be a thread (archived, or not
 * loaded): fetch it before opening it, so a search result or a link
 * reaches it. True when it is in the store now.
 */
export async function ensureChannel(id: Snowflake): Promise<boolean> {
  if (useData.getState().channels[id]) return true;
  try {
    const c = await api.client.get<Channel>(Routes.channel(id));
    if (!isThreadChannelType(c.type)) return false;
    updateData((s) => putThread(s, c));
    return true;
  } catch {
    return false;
  }
}

/** Opens a channel or a thread by ID (search results, jumps): a thread not held is fetched first. */
export function openChannelById(id: Snowflake, then?: () => void): void {
  if (useData.getState().channels[id]) {
    goHub({ kind: 'channel', channelId: id });
    then?.();
    return;
  }
  void ensureChannel(id).then((ok) => {
    if (!ok) return;
    goHub({ kind: 'channel', channelId: id });
    then?.();
  });
}
